import React, { useState, useEffect, useRef } from 'react';
import ReactDOM from 'react-dom';
import { useParams } from 'react-router-dom';
import { useMDCockpit } from '../../../../hooks/useMDCockpit';
import { useCesium } from '../../../../context/CesiumContext';
import type { GSCoordType, GSAltDatum, GSFovGeometry } from '../../../../types/mission';
import { createGroundStation as apiCreateGroundStation, updateGroundStation as apiUpdateGroundStation } from '../../../../service/groundStationApi';
import { NAME_MAX_LENGTH, enforceNameConstraints, checkSpecialCharacters } from '../../../../lib/validation';
import { showToastMessage, preventKeyInputs } from '../../../../../../../utils/commonFunctions';
import { ecefToGeodetic, geodeticToEcef, geodeticToGeocentric, geocentricToGeodetic } from '../../../../lib/coordinates';
import styles from './AddGroundStationModal.module.scss';
import GroundStationService from '../../../../../../../service/GroundStationService';

const limitDecimalPlaces = (valStr: string, maxDecimals: number = 8): string => {
  if (!valStr.includes('.')) return valStr;
  const parts = valStr.split('.');
  if (parts[1].length > maxDecimals) {
    return `${parts[0]}.${parts[1].substring(0, maxDecimals)}`;
  }
  return valStr;
};

const DishIcon = () => (
  <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
    <path d="M9.73124 2.75095C10.1726 2.71459 10.612 2.84218 10.9652 3.10935C11.3185 3.37651 11.5608 3.7645 11.646 4.19914M9.61543 3.5011C9.92706 3.46103 10.242 3.54475 10.4926 3.73428C10.7432 3.92382 10.9095 4.20406 10.9558 4.51483" stroke="currentColor" strokeWidth="0.498568" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M6.11026 4.12044L6.38696 4.32971M8.69185 6.07293L6.38696 4.32971M8.69185 6.07293L10.9967 7.81616M8.69185 6.07293L9.79253 4.61754M6.38696 4.32971C5.2733 5.80226 5.53436 7.2844 6.96013 8.36273C8.38591 9.44107 9.88309 9.28871 10.9967 7.81616M11.2734 8.02543L10.9967 7.81616" stroke="currentColor" strokeWidth="0.62321" strokeLinecap="round" />
    <path d="M9.83398 11.9507L9.48706 11.9507M9.48706 11.9507L7.02688 11.9507L4.13703 11.9507M9.48706 11.9507L5.36723 8.47319C4.88102 8.06279 4.13702 8.40841 4.13703 9.04468L4.13703 11.9507M3.79011 11.9507L4.13703 11.9507" stroke="currentColor" strokeWidth="0.623208" strokeLinecap="round" />
    <ellipse cx="5.14928" cy="9.64587" rx="0.397326" ry="0.397336" fill="currentColor" />
  </svg>
);

const CloseIcon = () => (
  <svg width="14" height="14" viewBox="0 0 14 14" fill="none">
    <path d="M3 3l8 8M11 3l-8 8" stroke="currentColor" strokeWidth="1.4" strokeLinecap="round" />
  </svg>
);

const MinimizeIcon = ({ minimized = false }: { minimized?: boolean }) => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d={minimized ? 'M6 2v8M2 6h8' : 'M2 6h8'} stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" />
  </svg>
);

const ChevronDown = () => (
  <svg width="9" height="9" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5">
    <path d="m6 9 6 6 6-6" />
  </svg>
);

const ChevronIcon = () => (
  <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <path d="M3 4.5l3 3 3-3" stroke="currentColor" strokeWidth="1.3" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

interface InlineTriggerProps {
  value: string;
  options: { value: string; label: string }[];
  onChange: (v: string) => void;
  variant?: 'inline' | 'select';
  style?: React.CSSProperties;
}

const InlineTrigger: React.FC<InlineTriggerProps> = ({ value, options, onChange, variant = 'inline', style }) => {
  const [open, setOpen] = useState(false);
  const [triggerRect, setTriggerRect] = useState<DOMRect | null>(null);
  const ref = useRef<HTMLSpanElement>(null);
  const dropRef = useRef<HTMLDivElement>(null);

  const handleClick = () => {
    if (!open && ref.current) {
      setTriggerRect(ref.current.getBoundingClientRect());
    }
    setOpen(o => !o);
  };

  useEffect(() => {
    const handler = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!ref.current?.contains(t) && !dropRef.current?.contains(t)) setOpen(false);
    };
    if (open) document.addEventListener('mousedown', handler, true);
    return () => document.removeEventListener('mousedown', handler, true);
  }, [open]);

  const label = options.find(o => o.value === value)?.label ?? value;
  const isSelect = variant === 'select';
  const triggerClass = isSelect ? styles.selectTriggerBox : styles.sectionTrigger;

  return (
    <span ref={ref} className={triggerClass} onClick={handleClick} style={style}>
      <span className={isSelect ? styles.selectTriggerText : ''}>{label}</span>
      <ChevronDown />
      {open && triggerRect && ReactDOM.createPortal(
        <div
          ref={dropRef}
          className={styles.dropdownPop}
          style={{
            position: 'fixed',
            top: triggerRect.bottom + 4,
            left: isSelect ? triggerRect.left : triggerRect.right - 140, // align left for select box, right for inline
            width: isSelect ? triggerRect.width : 140,
            zIndex: 9999,
          }}
          onClick={e => e.stopPropagation()}
        >
          {options.map(o => (
            <div
              key={o.value}
              className={`${styles.dropdownOpt} ${o.value === value ? styles.dropdownOptActive : ''}`}
              onClick={(e) => { e.stopPropagation(); onChange(o.value); setOpen(false); }}
            >
              {o.label}
            </div>
          ))}
        </div>,
        document.body
      )}
    </span>
  );
};

function toKm(v: string, unit: 'km' | 'm'): number {
  const n = parseFinite(v) ?? 0;
  return unit === 'm' ? n / 1000 : n;
}

function parseFinite(value: string): number | null {
  if (value.trim() === '') return null;
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : null;
}


const COORD_TYPES: { value: GSCoordType; label: string }[] = [
  { value: 'geodetic', label: 'Geodetic' },
  { value: 'geocentric', label: 'Geocentric' },
  { value: 'cartesian', label: 'Cartesian' },
];

const FOV_GEOMS: { value: GSFovGeometry; label: string }[] = [
  { value: 'conic', label: 'Conic' },
  { value: 'rectangular', label: 'Rectangular' },
  { value: 'elliptical', label: 'Elliptical' },
];

const AddGroundStationModal: React.FC = () => {
  const {
    addGSModalOpen, closeAddGS, fetchGroundStations, leftPanelCollapsed,
    editingGSId, groundStations, updateGroundStation, invalidateAccessWindows,
  } = useMDCockpit();
  const dragStyle = {
    position: 'absolute' as const,
    top: 60,
    left: leftPanelCollapsed ? 12 : 236,
  };
  const cesium = useCesium();
  const { mission_id, scenario_id } = useParams<{ mission_id: string; scenario_id: string }>();

  // ── Identification ────────────────────────────────────
  const [name, setName] = useState(() => `Ground Station ${String(groundStations.length + 1).padStart(2, '0')}`);

  // ── Coordinate type ───────────────────────────────────
  const [coordType, setCoordType] = useState<GSCoordType>('geodetic');

  // ── Geodetic / Geocentric ─────────────────────────────
  const [lat, setLat] = useState('78.2297');
  const [lon, setLon] = useState('15.6457');
  const [alt, setAlt] = useState('0');
  const [altDatum, setAltDatum] = useState<GSAltDatum>('MSL');
  const [altUnit, setAltUnit] = useState<'km' | 'm'>('km');

  // ── Cartesian ─────────────────────────────────────────
  const [xVal, setXVal] = useState('');
  const [yVal, setYVal] = useState('');
  const [zVal, setZVal] = useState('');
  const [cartUnit, setCartUnit] = useState<'km' | 'm'>('km');

  // ── Common ────────────────────────────────────────────
  const [minElevation, setMinElevation] = useState('5');

  // ── FOV ───────────────────────────────────────────────
  const [fovOpen, setFovOpen] = useState(true);
  const [fovGeometry, setFovGeometry] = useState<GSFovGeometry>('conic');
  const [fov, setFov] = useState('45');
  const [fovH, setFovH] = useState('45');
  const [fovV, setFovV] = useState('45');
  const [pointingAz, setPointingAz] = useState('0');
  const [pointingEle, setPointingEle] = useState('90');

  const [errors, setErrors] = useState<Record<string, string>>({});
  const clearErr = (key: string) => setErrors(p => {
    if (!p[key]) return p;
    const n = { ...p };
    delete n[key];
    return n;
  });
  const [loading, setLoading] = useState(false);
  const [minimized, setMinimized] = useState(false);
  const [isMapPicking, setIsMapPicking] = useState(false);

  useEffect(() => {
    if (addGSModalOpen) {
      setMinimized(false);
    }
  }, [addGSModalOpen]);

  const handleCoordTypeChange = (newType: GSCoordType) => {
    if (newType === coordType) return;

    const parseVal = (v: string) => {
      const parsed = parseFloat(v);
      return isNaN(parsed) ? 0 : parsed;
    };

    let currentGeodeticLat = 0;
    let currentGeodeticLon = 0;
    let currentAltKm = 0;

    if (coordType === 'cartesian') {
      const xKm = toKm(xVal, cartUnit);
      const yKm = toKm(yVal, cartUnit);
      const zKm = toKm(zVal, cartUnit);
      const geo = ecefToGeodetic(xKm, yKm, zKm);
      currentGeodeticLat = geo.lat;
      currentGeodeticLon = geo.lon;
      currentAltKm = geo.altKm;
    } else if (coordType === 'geocentric') {
      currentGeodeticLat = geocentricToGeodetic(parseVal(lat));
      currentGeodeticLon = parseVal(lon);
      currentAltKm = toKm(alt, altUnit);
    } else {
      currentGeodeticLat = parseVal(lat);
      currentGeodeticLon = parseVal(lon);
      currentAltKm = toKm(alt, altUnit);
    }

    if (newType === 'cartesian') {
      const ecef = geodeticToEcef(currentGeodeticLat, currentGeodeticLon, currentAltKm);
      const factor = cartUnit === 'm' ? 1000 : 1;
      setXVal(String(+(ecef.x * factor).toFixed(3)));
      setYVal(String(+(ecef.y * factor).toFixed(3)));
      setZVal(String(+(ecef.z * factor).toFixed(3)));
    } else if (newType === 'geocentric') {
      const geocentricLat = geodeticToGeocentric(currentGeodeticLat);
      setLat(String(+geocentricLat.toFixed(6)));
      setLon(String(+currentGeodeticLon.toFixed(6)));
      const altFactor = altUnit === 'm' ? 1000 : 1;
      setAlt(String(+(currentAltKm * altFactor).toFixed(3)));
    } else {
      setLat(String(+currentGeodeticLat.toFixed(6)));
      setLon(String(+currentGeodeticLon.toFixed(6)));
      const altFactor = altUnit === 'm' ? 1000 : 1;
      setAlt(String(+(currentAltKm * altFactor).toFixed(3)));
    }

    setCoordType(newType);
  };

  // ── Public Ground Stations ────────────────────────────
  const [publicStations, setPublicStations] = useState<any[]>([]);
  const [selectedProvider, setSelectedProvider] = useState('custom');
  const [selectedStationId, setSelectedStationId] = useState('');
  const [savedProvider, setSavedProvider] = useState('');
  const [savedFreqbands, setSavedFreqbands] = useState<string[]>(['S-band']);

  const providers = React.useMemo(() => {
    const s = new Set<string>();
    publicStations.forEach(gs => {
      const p = gs.properties?.provider;
      if (p) s.add(p);
    });
    return Array.from(s).sort();
  }, [publicStations]);

  const filteredStations = React.useMemo(() => {
    if (selectedProvider === 'custom') return [];
    return publicStations.filter(gs => gs.properties?.provider === selectedProvider);
  }, [publicStations, selectedProvider]);

  const resetForm = () => {
    const nextIndex = groundStations.length + 1;
    setName(`Ground Station ${String(nextIndex).padStart(2, '0')}`);
    setCoordType('geodetic');
    setLat('78.2297'); setLon('15.6457'); setAlt('0');
    setAltDatum('MSL'); setAltUnit('km');
    setXVal(''); setYVal(''); setZVal(''); setCartUnit('km');
    setMinElevation('5');
    setFovOpen(true); setFovGeometry('conic');
    setFov('45'); setFovH('45'); setFovV('45');
    setPointingAz('0'); setPointingEle('90');
    setErrors({});
    setIsMapPicking(false);
    setSelectedProvider('custom');
    setSelectedStationId('');
    setSavedProvider('');
    setSavedFreqbands(['S-band']);
  };

  useEffect(() => {
    if (addGSModalOpen) {
      GroundStationService.getGroundStationsByMissionId()
        .then((res) => {
          const list = res?.data?.data ?? res?.data ?? [];
          setPublicStations(list);
        })
        .catch((err) => {
          console.error('[AddGSModal] Failed to fetch public ground stations:', err);
        });
    }
  }, [addGSModalOpen]);

  useEffect(() => {
    if (addGSModalOpen && editingGSId && publicStations.length > 0) {
      const gs = groundStations.find(g => g.id === editingGSId);
      if (gs) {
        const existingProvider = gs.provider || 'custom';
        const matchedStation = publicStations.find(
          s => s.properties?.provider === existingProvider && s.groundStationName === gs.name
        );
        if (matchedStation) {
          setSelectedStationId(matchedStation.groundStationId);
        } else {
          setSelectedStationId('');
        }
      }
    }
  }, [addGSModalOpen, editingGSId, publicStations, groundStations]);

  const handleStationChange = (stationId: string) => {
    setSelectedStationId(stationId);
    if (!stationId || stationId === 'all') return;

    const station = publicStations.find(s => s.groundStationId === stationId);
    if (!station) return;

    setName(station.groundStationName || '');

    const props = station.properties || {};

    // Set coordinates
    const latVal = props.latitude != null ? String(props.latitude) : '';
    const lonVal = props.longitude != null ? String(props.longitude) : '';
    const altVal = props.altitude != null ? String(props.altitude) : '0';
    setLat(latVal);
    setLon(lonVal);
    setAlt(altVal);
    setAltDatum(props.altitudeReference || 'WGS84');
    setCoordType('geodetic'); // Auto-fill as geodetic
    setAltUnit('km');

    // Min elevation
    setMinElevation(props.minElevationAngle != null ? String(props.minElevationAngle) : '5');

    // FOV geometry
    const rawGeom = props.FieldOfView?.geometry?.toLowerCase();
    const geom: GSFovGeometry = rawGeom === 'conical' ? 'conic' : (rawGeom || 'conic');
    setFovGeometry(geom);
    setFov(props.FieldOfView?.conicFOV != null ? String(props.FieldOfView.conicFOV) : '45');
    setFovH(props.FieldOfView?.horizontalFOV != null ? String(props.FieldOfView.horizontalFOV) : '45');
    setFovV(props.FieldOfView?.verticalFOV != null ? String(props.FieldOfView.verticalFOV) : '45');

    // Pointing Axis
    setPointingAz(props.FieldOfView?.pointingAxis?.azimuth != null ? String(props.FieldOfView.pointingAxis.azimuth) : '0');
    setPointingEle(props.FieldOfView?.pointingAxis?.elevation != null ? String(props.FieldOfView.pointingAxis.elevation) : '90');

    // Provider & Freqbands
    setSavedProvider(props.provider || '');
    setSavedFreqbands(props.freqbands || ['S-band']);

    // Rotate map and center on the station (without changing zoom/height)
    const latNum = parseFloat(latVal);
    const lonNum = parseFloat(lonVal);
    if (Number.isFinite(latNum) && Number.isFinite(lonNum)) {
      cesium.centerOnPoint?.(lonNum, latNum);
    }
  };

  useEffect(() => {
    if (!addGSModalOpen) {
      cesium.removeDraftGroundStation();
      return;
    }

    cesium.removeDraftGroundStation();
    console.log('[AddGSModal] Modal opened - addGSModalOpen:', addGSModalOpen, 'editingGSId:', editingGSId);
    if (editingGSId) {
      const gs = groundStations.find(g => g.id === editingGSId);
      if (gs) {
        setName(gs.name || '');
        setCoordType(gs.coordType || 'geodetic');
        setLat(gs.lat != null ? String(gs.lat) : '');
        setLon(gs.lon != null ? String(gs.lon) : '');
        setAlt(gs.altKm != null ? String(gs.altKm) : '0');
        setAltDatum(gs.altDatum || 'MSL');
        setAltUnit('km');
        setXVal(gs.x != null ? String(gs.x) : '');
        setYVal(gs.y != null ? String(gs.y) : '');
        setZVal(gs.z != null ? String(gs.z) : '');
        setCartUnit('km');
        setMinElevation(gs.minElevation != null ? String(gs.minElevation) : '5');

        const rawGm = gs.fovGeometry ?? 'conic';
        const normGm: GSFovGeometry = (rawGm as string) === 'conical' ? 'conic' : rawGm as GSFovGeometry;
        setFovGeometry(normGm);
        setFov(gs.fovDeg != null ? String(gs.fovDeg) : '45');
        setFovH(gs.fovH != null ? String(gs.fovH) : '45');
        setFovV(gs.fovV != null ? String(gs.fovV) : '45');
        setPointingAz(gs.pointingAz != null ? String(gs.pointingAz) : '');
        setPointingEle(gs.pointingEle != null ? String(gs.pointingEle) : '');
        setMinimized(false);

        const existingProvider = gs.provider || 'custom';
        setSelectedProvider(existingProvider);
        setSavedProvider(gs.provider || '');
        setSavedFreqbands(gs.freqbands || ['S-band']);
      }
    } else {
      resetForm();
      setMinimized(false);
    }
  }, [addGSModalOpen, editingGSId, groundStations]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (!addGSModalOpen || minimized) {
      setIsMapPicking(false);
      return;
    }

    setIsMapPicking(true);
    cesium.startPointDraw((pickedLat, pickedLon) => {
      if (coordType === 'cartesian') {
        const ecef = geodeticToEcef(pickedLat, pickedLon, 0);
        const factor = cartUnit === 'm' ? 1000 : 1;
        setXVal(String(+(ecef.x * factor).toFixed(3)));
        setYVal(String(+(ecef.y * factor).toFixed(3)));
        setZVal(String(+(ecef.z * factor).toFixed(3)));
        setErrors(prev => {
          const next = { ...prev };
          delete next.x;
          delete next.y;
          delete next.z;
          return next;
        });
      } else {
        setLat(String(+pickedLat.toFixed(6)));
        setLon(String(+pickedLon.toFixed(6)));
        setErrors(prev => {
          const next = { ...prev };
          delete next.lat;
          delete next.lon;
          return next;
        });
      }
      setIsMapPicking(false);
    });

    return () => {
      setIsMapPicking(false);
      cesium.stopDraw();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [addGSModalOpen, minimized, coordType, cartUnit]);



  // Recalculate FOV based on Minimum Elevation Angle when geometry is Conic
  useEffect(() => {
    if (fovGeometry === 'conic') {
      const minEleVal = parseFinite(minElevation) ?? 5;
      const calculatedFov = 180 - minEleVal * 2;
      setFov(String(calculatedFov));
      clearErr('fov');
    }
  }, [minElevation, fovGeometry]);

  const isFormInvalid = React.useMemo(() => {
    if (loading) return true;
    if (selectedStationId === 'all') {
      return filteredStations.length === 0;
    }

    if (!name.trim()) return true;

    if (coordType === 'cartesian') {
      if (!xVal.trim() || !yVal.trim() || !zVal.trim()) return true;
      const x = parseFinite(xVal);
      const y = parseFinite(yVal);
      const z = parseFinite(zVal);
      if (x === null || y === null || z === null) return true;
      if (Math.hypot(x, y, z) <= 0) return true;
    } else {
      if (!lat.trim() || !lon.trim() || !alt.trim()) return true;
      const latN = parseFinite(lat);
      const lonN = parseFinite(lon);
      const altN = parseFinite(alt);
      if (latN === null || latN < -90 || latN > 90) return true;
      if (lonN === null || lonN < -180 || lonN > 180) return true;
      if (altN === null) return true;
    }

    const elev = parseFinite(minElevation);
    if (elev === null || elev < 0 || elev > 90) return true;

    if (fovGeometry === 'conic') {
      const fovN = parseFinite(fov);
      if (fovN === null || fovN <= 0 || fovN >= 178) return true;
    } else {
      const h = parseFinite(fovH);
      const v = parseFinite(fovV);
      if (h === null || h <= 0 || h >= 178) return true;
      if (v === null || v <= 0 || v >= 178) return true;
    }

    const az = parseFinite(pointingAz);
    const ele = parseFinite(pointingEle);
    if (az === null || az < -360 || az > 360) return true;
    if (ele === null || ele < -90 || ele > 90) return true;

    return false;
  }, [
    loading, selectedStationId, filteredStations, name, coordType,
    xVal, yVal, zVal, lat, lon, alt, minElevation,
    fovGeometry, fov, fovH, fovV, pointingAz, pointingEle,
  ]);

  // Live map preview
  useEffect(() => {
    if (!addGSModalOpen) return;

    if (selectedStationId === 'all') {
      const drafts = filteredStations.map(station => {
        const props = station.properties || {};
        const latVal = props.latitude != null ? parseFloat(props.latitude) : Number.NaN;
        const lonVal = props.longitude != null ? parseFloat(props.longitude) : Number.NaN;

        return {
          lat: latVal,
          lon: lonVal,
          altKm: 0,
          minElevation: 5,
          fovDeg: 45,
          fovGeometry: 'conic',
          fovH: 45,
          fovV: 45,
          pointingAz: 0,
          pointingEle: 90,
        };
      }).filter(d => Number.isFinite(d.lat) && Number.isFinite(d.lon));

      cesium.updateDraftGroundStation(drafts);
      return;
    }

    let previewLat: number, previewLon: number, previewAltKm: number;

    if (coordType === 'cartesian') {
      if (xVal.trim() === '' || yVal.trim() === '' || zVal.trim() === '') {
        cesium.removeDraftGroundStation();
        return;
      }
      const geo = ecefToGeodetic(toKm(xVal, cartUnit), toKm(yVal, cartUnit), toKm(zVal, cartUnit));
      previewLat = geo.lat; previewLon = geo.lon; previewAltKm = geo.altKm;
    } else {
      previewLat = parseFinite(lat) ?? Number.NaN; previewLon = parseFinite(lon) ?? Number.NaN;
      previewAltKm = toKm(alt, altUnit);
    }

    if (!Number.isFinite(previewLat) || !Number.isFinite(previewLon) || previewLat < -90 || previewLat > 90 || previewLon < -180 || previewLon > 180) {
      cesium.removeDraftGroundStation();
      return;
    }

    const previewFovDeg = fovGeometry === 'conic'
      ? parseFinite(fov) ?? 45
      : Math.max(parseFinite(fovH) ?? 45, parseFinite(fovV) ?? 45);

    cesium.updateDraftGroundStation({
      lat: previewLat,
      lon: previewLon,
      altKm: previewAltKm,
      minElevation: parseFinite(minElevation) ?? 5,
      fovDeg: previewFovDeg,
      fovGeometry,
      fovH: parseFinite(fovH) ?? 45,
      fovV: parseFinite(fovV) ?? 45,
      pointingAz: parseFinite(pointingAz) ?? 0,
      pointingEle: parseFinite(pointingEle) ?? 90,
    });
  }, [lat, lon, alt, altUnit, xVal, yVal, zVal, cartUnit, coordType, minElevation, fov, fovH, fovV, fovGeometry, pointingAz, pointingEle, addGSModalOpen, selectedStationId, filteredStations]); // eslint-disable-line react-hooks/exhaustive-deps

  if (!addGSModalOpen) return null;

  const sanitizeNumericInput = (inputValue: string, label?: string) => {
    if (inputValue === '') return '';

    const hasInvalidChars = /[^0-9.-]/.test(inputValue);
    const minusIndex = inputValue.indexOf('-');
    const hasInvalidMinus = minusIndex > 0 || (minusIndex === 0 && inputValue.lastIndexOf('-') > 0);
    const periods = (inputValue.match(/\./g) || []).length;
    const hasInvalidPeriod = periods > 1;

    if (hasInvalidChars || hasInvalidMinus || hasInvalidPeriod) {
      showToastMessage('info', `Only numeric values are allowed in ${label || 'field'}`);

      let cleaned = '';
      let decimalCount = 0;
      for (let i = 0; i < inputValue.length; i++) {
        const char = inputValue[i];
        if (char === '-' && i === 0) {
          cleaned += char;
        } else if (char === '.') {
          if (decimalCount === 0) {
            cleaned += char;
            decimalCount++;
          }
        } else if (char >= '0' && char <= '9') {
          cleaned += char;
        }
      }
      return cleaned;
    }
    return inputValue;
  };

  const handleNumericKeyDown = (
    e: React.KeyboardEvent<HTMLInputElement>,
    value: string,
    onChange: (v: string) => void,
    options?: { min?: number; max?: number; label?: string }
  ) => {
    const allowNegative = options?.min !== undefined && options.min < 0;
    const keysToPrevent = allowNegative ? ['e', 'E', '+'] : ['e', 'E', '+', '-'];
    if (!preventKeyInputs(e, keysToPrevent)) return;

    if (e.key === 'ArrowUp' || e.key === 'ArrowDown') {
      e.preventDefault();
      const numValue = parseFloat(value);

      let step = 1;
      if (e.shiftKey) {
        step = 10;
      } else if (e.altKey || e.ctrlKey) {
        step = 0.1;
      }

      if (isNaN(numValue)) {
        onChange(e.key === 'ArrowUp' ? step.toString() : (-step).toString());
      } else {
        const decimals = value.includes('.') ? value.split('.')[1].length : 0;
        const targetDecimals = Math.min(Math.max(decimals, step === 0.1 ? 1 : 0), 10);
        const newValue = e.key === 'ArrowUp' ? numValue + step : numValue - step;

        if (options) {
          if ((options.min !== undefined && newValue < options.min) || (options.max !== undefined && newValue > options.max)) {
            showToastMessage('info', `${options.label || 'Value'} must be between ${options.min} and ${options.max}`);
            return;
          }
        }

        onChange(parseFloat(newValue.toFixed(targetDecimals)).toString());
      }
    }
  };

  const validate = () => {
    const errs: Record<string, string> = {};
    if (!name.trim()) errs.name = 'Ground Station name is required';

    if (coordType !== 'cartesian') {
      const latN = parseFinite(lat);
      if (latN === null) errs.lat = 'Latitude is required';
      else if (latN < -90 || latN > 90) errs.lat = 'Latitude must be between -90 and 90°';
      const lonN = parseFinite(lon);
      if (lonN === null) errs.lon = 'Longitude is required';
      else if (lonN < -180 || lonN > 180) errs.lon = 'Longitude must be between -180 and 180°';

      const altN = parseFinite(alt);
      if (altN === null) errs.alt = 'Altitude is required';
    } else {
      const x = parseFinite(xVal);
      const y = parseFinite(yVal);
      const z = parseFinite(zVal);
      if (x === null) errs.x = 'X coordinate is required';
      if (y === null) errs.y = 'Y coordinate is required';
      if (z === null) errs.z = 'Z coordinate is required';
      if (x !== null && y !== null && z !== null && Math.hypot(x, y, z) <= 0) errs.x = 'Invalid position vector';
    }

    const elev = parseFinite(minElevation);
    if (elev === null) errs.minElevation = 'Minimum Elevation Angle is required';
    else if (elev < 0 || elev > 90) errs.minElevation = 'Minimum Elevation Angle must be between 0 and 90°';

    if (fovGeometry === 'conic') {
      const fovN = parseFinite(fov);
      if (fovN === null) errs.fov = 'FOV is required';
      else if (fovN <= 0 || fovN >= 178) errs.fov = 'FOV must be between 0 and 178°';
    } else {
      const h = parseFinite(fovH);
      const v = parseFinite(fovV);
      if (h === null) errs.fovH = 'Horizontal FOV is required';
      else if (h <= 0 || h >= 178) errs.fovH = 'Horizontal FOV must be between 0 and 178°';
      if (v === null) errs.fovV = 'Vertical FOV is required';
      else if (v <= 0 || v >= 178) errs.fovV = 'Vertical FOV must be between 0 and 178°';
    }

    const az = parseFinite(pointingAz);
    const ele = parseFinite(pointingEle);
    if (az === null) errs.pointingAz = 'Pointing Azimuth is required';
    else if (az < -360 || az > 360) errs.pointingAz = 'Pointing Azimuth must be between -360 and 360°';
    if (ele === null) errs.pointingEle = 'Pointing Elevation is required';
    else if (ele < -90 || ele > 90) errs.pointingEle = 'Pointing Elevation must be between -90 and 90°';

    return errs;
  };

  const handleCreate = async () => {
    const errs = validate();
    if (Object.keys(errs).length > 0) {
      setErrors(errs);
      const firstError = Object.values(errs).find(Boolean);
      if (firstError) {
        showToastMessage('error', firstError);
      }
      return;
    }

    const xKm = toKm(xVal, cartUnit);
    const yKm = toKm(yVal, cartUnit);
    const zKm = toKm(zVal, cartUnit);

    const GEOM_MAP: Record<GSFovGeometry, string> = {
      conic: 'Conical',
      rectangular: 'Rectangular',
      elliptical: 'Elliptical',
    };
    const COORD_LABEL: Record<GSCoordType, string> = {
      geodetic: 'Geodetic',
      geocentric: 'Geocentric',
      cartesian: 'Cartesian',
    };

    const geoFromCart = coordType === 'cartesian' ? ecefToGeodetic(xKm, yKm, zKm) : null;

    const apiBody = {
      groundStationName: name.trim(),
      status: 'Configure',
      antennaId: [''],
      properties: {
        ...(coordType !== 'cartesian' ? {
          latitude: lat,
          longitude: lon,
          altitude: alt,
          altitudeReference: altDatum,
        } : {
          latitude: String(geoFromCart?.lat != null ? +geoFromCart.lat.toFixed(6) : 0),
          longitude: String(geoFromCart?.lon != null ? +geoFromCart.lon.toFixed(6) : 0),
          altitude: String(geoFromCart?.altKm != null ? +geoFromCart.altKm.toFixed(3) : 0),
          altitudeReference: 'WGS84',
          cartesian: [xKm, yKm, zKm] as [number, number, number],
        }),
        coordinateUnit: coordType === 'cartesian' ? cartUnit : altUnit,
        coordinateType: COORD_LABEL[coordType],
        minElevationAngle: parseFinite(minElevation) ?? 5,
        FieldOfView: {
          geometry: GEOM_MAP[fovGeometry],
          ...(fovGeometry === 'conic'
            ? { conicFOV: parseFinite(fov) ?? 45 }
            : { horizontalFOV: parseFinite(fovH) ?? 45, verticalFOV: parseFinite(fovV) ?? 45 }
          ),
          pointingAxis: {
            azimuth: parseFinite(pointingAz) ?? 0,
            elevation: parseFinite(pointingEle) ?? 90,
          },
        },
        freqbands: savedFreqbands,
        provider: savedProvider,
      },
    };

    try {
      if (!mission_id || !scenario_id) throw new Error('Missing mission or scenario id');
      setLoading(true);
      console.log('[AddGSModal] Submitting - editingGSId:', editingGSId, 'typeof:', typeof editingGSId, 'Name:', name);

      // Safety check: editingGSId must be a non-empty string for updates, or falsy for creates
      const isValidId = editingGSId && typeof editingGSId === 'string' && editingGSId.trim().length > 0;

      let isGSNameOnly = false;
      if (isValidId) {
        const gs = groundStations.find(g => g.id === editingGSId);
        if (gs) {
          isGSNameOnly =
            Number(gs.lat) === Number(lat) &&
            Number(gs.lon) === Number(lon) &&
            Number(gs.altKm) === Number(alt) &&
            Number(gs.minElevation) === (parseFinite(minElevation) ?? 5) &&
            gs.fovGeometry === fovGeometry &&
            gs.coordType === coordType;
        }
      }

      let res;
      if (isValidId) {
        console.log('[AddGSModal] UPDATE mode - calling PATCH with ID:', editingGSId);
        res = await apiUpdateGroundStation(mission_id, scenario_id, editingGSId, apiBody);
        const successMsg = res?.data?.message || res?.message || 'Ground station updated successfully';
        showToastMessage('success', successMsg);
      } else {
        console.log('[AddGSModal] CREATE mode - calling POST');
        if (editingGSId) {
          console.error('[AddGSModal] WARNING: editingGSId is not a valid string, treating as create. Value:', editingGSId, 'Type:', typeof editingGSId);
        }
        if (selectedStationId === 'all') {
          console.log('[AddGSModal] CREATE ALL mode - sequentially creating all stations for provider:', selectedProvider);
          for (const station of filteredStations) {
            const props = station.properties || {};
            const latVal = props.latitude != null ? String(props.latitude) : '';
            const lonVal = props.longitude != null ? String(props.longitude) : '';

            const body = {
              groundStationName: station.groundStationName.trim(),
              status: 'Configure',
              antennaId: [''],
              properties: {
                latitude: latVal,
                longitude: lonVal,
                altitude: '0',
                altitudeReference: 'WGS84',
                coordinateUnit: 'km',
                coordinateType: 'Geodetic',
                minElevationAngle: 5,
                FieldOfView: {
                  geometry: 'Conical',
                  conicFOV: 45,
                  pointingAxis: {
                    azimuth: 0,
                    elevation: 90,
                  },
                },
                freqbands: props.freqbands || ['S-band'],
                provider: props.provider || selectedProvider || '',
              },
            };
            res = await apiCreateGroundStation(mission_id, scenario_id, body);
          }
          const successMsg = res?.data?.message || res?.message || `Created all ${filteredStations.length} ground stations successfully`;
          showToastMessage('success', successMsg);
        } else {
          res = await apiCreateGroundStation(mission_id, scenario_id, apiBody);
          const successMsg = res?.data?.message || res?.message || 'Ground station created successfully';
          showToastMessage('success', successMsg);
        }
      }

      invalidateAccessWindows();

      await fetchGroundStations(mission_id, scenario_id);
      handleClose();
    } catch (err: any) {
      console.error('[GroundStation] Submit failed:', err);
      const errorMsg = err?.response?.data?.message || err?.response?.data?.Message || err?.data?.message || err?.message || `Failed to ${editingGSId ? 'update' : 'create'} ground station.`;
      showToastMessage('error', errorMsg);
      setErrors({ api: errorMsg });
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setIsMapPicking(false);
    cesium.stopDraw();
    cesium.removeDraftGroundStation();
    resetForm();
    closeAddGS();
  };

  const isCart = coordType === 'cartesian';
  const isDualFov = fovGeometry !== 'conic';

  return (
    <div className={styles.panel} style={{ ...dragStyle }}>
      {/* ── Header ── */}
      <div className={styles.header}>
        <span className={styles.headerIcon}><DishIcon /></span>
        <span className={styles.headerTitle}>{editingGSId ? 'EDIT GROUND STATION' : 'GROUND STATION'}</span>
        <button className={styles.closeBtn} onClick={() => setMinimized(m => !m)} type="button" title={minimized ? 'Restore' : 'Minimize'} onPointerDown={e => e.stopPropagation()}>
          <MinimizeIcon minimized={minimized} />
        </button>
        <button className={styles.closeBtn} onClick={handleClose} type="button" onPointerDown={e => e.stopPropagation()}><CloseIcon /></button>
      </div>

      {isMapPicking && !minimized && (
        <div className={styles.drawBanner}>
          <span className={styles.bannerPulse} />
          <span>Click globe to place point</span>
        </div>
      )}

      {!minimized && (
        <>
          <div className={styles.body}>
            {/* Provider and Predefined Station selection */}
            <div className={styles.nameRow}>
              <span className={styles.lbl}>Provider</span>
              <div style={{ display: 'flex', gap: '8px', width: '210px' }}>
                <div style={{ flex: 1.25, minWidth: 0 }}>
                  <InlineTrigger
                    value={selectedProvider}
                    options={[
                      { value: 'custom', label: 'Custom' },
                      ...providers.map(p => ({ value: p, label: p }))
                    ]}
                    onChange={v => {
                      setSelectedProvider(v);
                      setSelectedStationId('');
                      if (v === 'custom') {
                        setSavedProvider('');
                        setSavedFreqbands(['S-band']);
                      }
                    }}
                    variant="select"
                    style={{ width: '100%' }}
                  />
                </div>
                {selectedProvider !== 'custom' && (
                  <div style={{ flex: 0.75, minWidth: 0 }}>
                    <InlineTrigger
                      value={selectedStationId}
                      options={[
                        { value: '', label: 'Station' },
                        { value: 'all', label: 'All' },
                        ...filteredStations.map(s => ({ value: s.groundStationId, label: s.groundStationName }))
                      ]}
                      onChange={handleStationChange}
                      variant="select"
                      style={{ width: '100%' }}
                    />
                  </div>
                )}
              </div>
            </div>

            {selectedStationId === 'all' ? (
              <div className={styles.bulkSummary}>
                <div className={styles.bulkHeader}>
                  Stations to Import ({filteredStations.length})
                </div>
                <div className={styles.bulkList}>
                  {filteredStations.map((station, index) => {
                    const props = station.properties || {};
                    const latVal = props.latitude != null ? `${props.latitude}°` : 'N/A';
                    const lonVal = props.longitude != null ? `${props.longitude}°` : 'N/A';
                    return (
                      <div key={station.groundStationId || index} className={styles.bulkItem}>
                        <span className={styles.bulkStationName}>{station.groundStationName}</span>
                        <span className={styles.bulkStationCoords}>
                          {latVal}, {lonVal}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            ) : (
              <>
                {/* Standalone Name Field with separator border */}
                <div className={styles.nameRow}>
                  <span className={styles.lbl}>Name</span>
                  <div className={`${styles.input} ${styles.text}`}>
                    <input
                      className={errors.name ? styles.inputError : ''}
                      type="text"
                      placeholder="e.g. Svalbard"
                      value={name}
                      maxLength={NAME_MAX_LENGTH}
                      onChange={e => {
                        const inputValue = e.target.value;
                        const enforced = enforceNameConstraints(inputValue);
                        setName(enforced);
                        clearErr('name');

                        // Check if character limit reached
                        if (enforced.length === NAME_MAX_LENGTH) {
                          showToastMessage('info', `Character limit of ${NAME_MAX_LENGTH} reached`);
                        }

                        // Check if special characters were rejected
                        if (inputValue !== enforced && inputValue.length > enforced.length) {
                          const specialCharError = checkSpecialCharacters(inputValue);
                          if (specialCharError) {
                            showToastMessage('info', specialCharError);
                          }
                        }
                      }}
                    />
                  </div>
                </div>

                {/* Geostation Details Header with Coordinate selection on the right */}
                <div className={styles.detailsHeader}>
                  <span className={styles.detailsTitle}>Geostation Details</span>
                  <InlineTrigger
                    value={coordType}
                    options={COORD_TYPES}
                    onChange={v => handleCoordTypeChange(v as GSCoordType)}
                  />
                </div>

                {!isCart ? (
                  <div className={styles.coordFieldsList}>
                    {/* Latitude */}
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabel}>Latitude *</span>
                      <div className={`${styles.segmentedInputGroup} ${errors.lat ? styles.inputBoxError : ''}`}>
                        <input
                          className={styles.segmentInput}
                          type="text"
                          inputMode="decimal"
                          placeholder="-90 to 90"
                          value={lat}
                          onKeyDown={e => handleNumericKeyDown(e, lat, setLat, { min: -90, max: 90, label: 'Latitude' })}
                          onChange={e => {
                            let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Latitude'));
                            const num = parseFloat(v);
                            if (!isNaN(num) && (num < -90 || num > 90)) {
                              showToastMessage('info', 'Latitude must be between -90 and 90°');
                              return;
                            }
                            setLat(v);
                            clearErr('lat');
                          }}
                        />
                        <span className={styles.segmentUnit}>°</span>
                      </div>
                    </div>

                    {/* Longitude */}
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabel}>Longitude *</span>
                      <div className={`${styles.segmentedInputGroup} ${errors.lon ? styles.inputBoxError : ''}`}>
                        <input
                          className={styles.segmentInput}
                          type="text"
                          inputMode="decimal"
                          placeholder="-180 to 180"
                          value={lon}
                          onKeyDown={e => handleNumericKeyDown(e, lon, setLon, { min: -180, max: 180, label: 'Longitude' })}
                          onChange={e => {
                            let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Longitude'));
                            const num = parseFloat(v);
                            if (!isNaN(num) && (num < -180 || num > 180)) {
                              showToastMessage('info', 'Longitude must be between -180 and 180°');
                              return;
                            }
                            setLon(v);
                            clearErr('lon');
                          }}
                        />
                        <span className={styles.segmentUnit}>°</span>
                      </div>
                    </div>

                    {/* Altitude */}
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabel}>Altitude *</span>
                      <div className={`${styles.threeSegmentInput} ${errors.alt ? styles.inputBoxError : ''}`}>
                        <span
                          className={styles.segmentToggleLeft}
                          title="Click to toggle MSL / WGS84"
                          onClick={() => setAltDatum(d => d === 'MSL' ? 'WGS84' : 'MSL')}
                        >
                          {altDatum}
                        </span>
                        <input
                          className={styles.segmentInputMiddle}
                          type="text"
                          inputMode="decimal"
                          value={alt}
                          onKeyDown={e => { preventKeyInputs(e, ['e', 'E', '+', '-']) }}
                          onChange={e => {
                            let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, `Altitude (${altUnit})`));
                            const num = parseFloat(v);
                            const maxAlt = altUnit === 'km' ? 10 : 10000;
                            if (!isNaN(num) && (num < 0 || num > maxAlt)) {
                              showToastMessage('info', `Altitude must be between ${0} and ${maxAlt} ${altUnit}`);
                              return;
                            }
                            setAlt(v);
                            clearErr('alt');
                          }}
                        />
                        <span
                          className={styles.segmentToggleRight}
                          title="Click to toggle km / m"
                          onClick={() => {
                            const newUnit = altUnit === 'km' ? 'm' : 'km';
                            if (alt !== '') {
                              const num = parseFloat(alt);
                              if (!isNaN(num)) {
                                setAlt(
                                  newUnit === 'm'
                                    ? String(+(num * 1000).toFixed(3))
                                    : String(+(num / 1000).toFixed(6))
                                );
                              }
                            }
                            setAltUnit(newUnit);
                          }}
                        >
                          {altUnit === 'km' ? 'Km' : 'm'}
                        </span>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div className={styles.coordFieldsList}>
                    {/* X Position */}
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabel}>X Position *</span>
                      <div className={`${styles.segmentedInputGroup} ${errors.x ? styles.inputBoxError : ''}`}>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="x coordinate"
                          value={xVal}
                          onKeyDown={e => handleNumericKeyDown(e, xVal, setXVal, { label: 'X' })}
                          onChange={e => { setXVal(limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'X'))); clearErr('x'); }}
                          className={styles.segmentInput}
                        />
                        <span className={styles.segmentUnit}>m</span>
                      </div>
                    </div>
                    {/* Y Position */}
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabel}>Y Position *</span>
                      <div className={`${styles.segmentedInputGroup} ${errors.y ? styles.inputBoxError : ''}`}>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="y coordinate"
                          value={yVal}
                          onKeyDown={e => handleNumericKeyDown(e, yVal, setYVal, { label: 'Y' })}
                          onChange={e => { setYVal(limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Y'))); clearErr('y'); }}
                          className={styles.segmentInput}
                        />
                        <span className={styles.segmentUnit}>m</span>
                      </div>
                    </div>
                    {/* Z Position */}
                    <div className={styles.fieldRow}>
                      <span className={styles.fieldLabel}>Z Position *</span>
                      <div className={`${styles.segmentedInputGroup} ${errors.z ? styles.inputBoxError : ''}`}>
                        <input
                          type="text"
                          inputMode="decimal"
                          placeholder="z coordinate"
                          value={zVal}
                          onKeyDown={e => handleNumericKeyDown(e, zVal, setZVal, { label: 'Z' })}
                          onChange={e => { setZVal(limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Z'))); clearErr('z'); }}
                          className={styles.segmentInput}
                        />
                        <span className={styles.segmentUnit}>m</span>
                      </div>
                    </div>
                  </div>
                )}

                {/* Minimum Elevation Angle (always shown under details) */}
                <div className={styles.fieldRow}>
                  <span className={styles.fieldLabel}>Minimum Elevation Angle</span>
                  <div className={`${styles.segmentedInputGroup} ${errors.minElevation ? styles.inputBoxError : ''}`}>
                    <input
                      className={styles.segmentInput}
                      type="text"
                      inputMode="decimal"
                      placeholder="37.5"
                      value={minElevation}
                      onKeyDown={e => handleNumericKeyDown(e, minElevation, setMinElevation, { min: 0, max: 90, label: 'Minimum Elevation Angle' })}
                      onChange={e => {
                        let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Minimum Elevation Angle'));
                        const num = parseFloat(v);
                        if (!isNaN(num) && (num < 0 || num > 90)) {
                          showToastMessage('info', 'Minimum Elevation Angle must be between 0 and 90°');
                          return;
                        }
                        setMinElevation(v);
                        clearErr('minElevation');
                      }}
                    />
                    <span className={styles.segmentUnit}>°</span>
                  </div>
                </div>

                {/* Field of View collapsible section */}
                <div className={styles.collapsibleSection}>
                  <div
                    className={styles.collapsibleHeader}
                    onClick={() => setFovOpen(o => !o)}
                  >
                    <span className={styles.detailsTitle}>Field of View</span>
                    <span className={`${styles.chevronIcon} ${!fovOpen ? styles.chevronOpen : ''}`}>
                      <ChevronIcon />
                    </span>
                  </div>

                  {fovOpen && (
                    <div className={styles.fovContent}>
                      {/* Geometry Selector */}
                      <div className={styles.fieldRow}>
                        <span className={styles.fieldLabel}>Geometry *</span>
                        <InlineTrigger
                          value={fovGeometry}
                          options={FOV_GEOMS}
                          onChange={v => setFovGeometry(v as GSFovGeometry)}
                          variant="select"
                        />
                      </div>

                      {/* Conic: single FOV */}
                      {!isDualFov && (
                        <div className={styles.fieldRow}>
                          <span className={styles.fieldLabel}>FOV *</span>
                          <div className={`${styles.segmentedInputGroup} ${errors.fov ? styles.inputBoxError : ''}`}>
                            <input
                              className={styles.segmentInput}
                              type="text"
                              inputMode="decimal"
                              value={fov}
                              onKeyDown={e => handleNumericKeyDown(e, fov, setFov, { min: 0.0001, max: 177.9999, label: 'FOV' })}
                              onChange={e => {
                                let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'FOV'));
                                const num = parseFloat(v);
                                if (!isNaN(num) && (num <= 0 || num >= 178)) {
                                  showToastMessage('info', 'FOV must be between 0 and 178°');
                                  return;
                                }
                                setFov(v);
                                clearErr('fov');
                              }}
                            />
                            <span className={styles.segmentUnit}>°</span>
                          </div>
                        </div>
                      )}

                      {/* Rectangular / Elliptical: H + V */}
                      {isDualFov && (
                        <>
                          <div className={styles.fieldRow}>
                            <span className={styles.fieldLabel}>Horizontal FOV *</span>
                            <div className={`${styles.segmentedInputGroup} ${errors.fovH ? styles.inputBoxError : ''}`}>
                              <input
                                className={styles.segmentInput}
                                type="text"
                                inputMode="decimal"
                                value={fovH}
                                onKeyDown={e => handleNumericKeyDown(e, fovH, setFovH, { min: 0.0001, max: 177.9999, label: 'Horizontal FOV' })}
                                onChange={e => {
                                  let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Horizontal FOV'));
                                  const num = parseFloat(v);
                                  if (!isNaN(num) && (num <= 0 || num >= 178)) {
                                    showToastMessage('info', 'Horizontal FOV must be between 0 and 178°');
                                    return;
                                  }
                                  setFovH(v);
                                  clearErr('fovH');
                                }}
                              />
                              <span className={styles.segmentUnit}>°</span>
                            </div>
                          </div>
                          <div className={styles.fieldRow}>
                            <span className={styles.fieldLabel}>Vertical FOV *</span>
                            <div className={`${styles.segmentedInputGroup} ${errors.fovV ? styles.inputBoxError : ''}`}>
                              <input
                                className={styles.segmentInput}
                                type="text"
                                inputMode="decimal"
                                value={fovV}
                                onKeyDown={e => handleNumericKeyDown(e, fovV, setFovV, { min: 0.0001, max: 177.9999, label: 'Vertical FOV' })}
                                onChange={e => {
                                  let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Vertical FOV'));
                                  const num = parseFloat(v);
                                  if (!isNaN(num) && (num <= 0 || num >= 178)) {
                                    showToastMessage('info', 'Vertical FOV must be between 0 and 178°');
                                    return;
                                  }
                                  setFovV(v);
                                  clearErr('fovV');
                                }}
                              />
                              <span className={styles.segmentUnit}>°</span>
                            </div>
                          </div>
                        </>
                      )}

                      {/* Pointing Axis */}
                      <div className={styles.fieldRow} style={{ marginTop: '4px' }}>
                        <span className={styles.fieldLabel}>Pointing Axis *</span>
                        <div className={styles.pointingContainer}>
                          {/* Az */}
                          <div className={`${styles.pointingSegment} ${errors.pointingAz ? styles.inputBoxError : ''}`}>
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="Az"
                              value={pointingAz}
                              onKeyDown={e => handleNumericKeyDown(e, pointingAz, setPointingAz, { min: -360, max: 360, label: 'Pointing Azimuth' })}
                              onChange={e => {
                                let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Pointing Azimuth'));
                                const num = parseFloat(v);
                                if (!isNaN(num) && (num < -360 || num > 360)) {
                                  showToastMessage('info', 'Pointing Azimuth must be between -360 and 360°');
                                  return;
                                }
                                setPointingAz(v);
                                clearErr('pointingAz');
                              }}
                              className={styles.pointingInput}
                            />
                            <span className={styles.pointingSuffix}>°</span>
                          </div>
                          {/* Ele */}
                          <div className={`${styles.pointingSegment} ${errors.pointingEle ? styles.inputBoxError : ''}`}>
                            <input
                              type="text"
                              inputMode="decimal"
                              placeholder="Ele"
                              value={pointingEle}
                              onKeyDown={e => handleNumericKeyDown(e, pointingEle, setPointingEle, { min: -90, max: 90, label: 'Pointing Elevation' })}
                              onChange={e => {
                                let v = limitDecimalPlaces(sanitizeNumericInput(e.target.value, 'Pointing Elevation'));
                                const num = parseFloat(v);
                                if (!isNaN(num) && (num < -90 || num > 90)) {
                                  showToastMessage('info', 'Pointing Elevation must be between -90 and 90°');
                                  return;
                                }
                                setPointingEle(v);
                                clearErr('pointingEle');
                              }}
                              className={styles.pointingInput}
                            />
                            <span className={styles.pointingSuffix}>°</span>
                          </div>
                        </div>
                      </div>
                    </div>
                  )}
                </div>
              </>
            )}
          </div>

          {errors.api && (
            <div style={{ padding: '0 12px 8px', fontSize: '10px', color: '#ff5555', fontFamily: 'Montserrat' }}>
              {errors.api}
            </div>
          )}

          <div className={styles.footer}>
            <button className={styles.cancelBtn} onClick={handleClose} type="button" disabled={loading}>
              Close
            </button>
            <button className={styles.createBtn} onClick={handleCreate} type="button" disabled={loading || isFormInvalid}>
              {loading
                ? (editingGSId ? 'Saving...' : 'Creating...')
                : (selectedStationId === 'all'
                  ? `Import All (${filteredStations.length})`
                  : (editingGSId ? 'Save' : 'Create'))}
            </button>
          </div>
        </>
      )}
    </div>
  );
};

export default AddGroundStationModal;
